import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowRight, Tag, Search, Filter } from 'lucide-react';
import { NavLink } from 'react-router-dom';

const blogPosts = [
  {
    id: 1,
    title: 'The Future of CROSSDOCS: AI-Powered Optimization',
    excerpt: 'Discover how artificial intelligence is revolutionizing CROSSDOCS operations, reducing processing times by up to 40% and improving accuracy to 99.9%.',
    author: 'Sarah Chen',
    date: 'March 15, 2024',
    readTime: '5 min read',
    category: 'Technology',
    image: 'assets/warehouse-8921538_1280.jpg',
    tags: ['AI', 'CROSSDOCS', 'Innovation']
  },
  {
    id: 2,
    title: 'Supply Chain Resilience: Lessons from Global Disruptions',
    excerpt: 'Learn the key strategies that helped leading companies maintain operations during recent supply chain challenges and how to implement them.',
    author: 'Michael Rodriguez',
    date: 'March 10, 2024',
    readTime: '7 min read',
    category: 'Strategy',
    image: 'assets/truck-1910871_1280.jpg',
    tags: ['Resilience', 'Strategy', 'Global Supply Chain']
  },
  {
    id: 3,
    title: 'Last-Mile Delivery: The Final Frontier of Customer Satisfaction',
    excerpt: 'Explore innovative approaches to last-mile delivery that are transforming customer experience and reducing delivery times.',
    author: 'Jennifer Park',
    date: 'March 5, 2024',
    readTime: '6 min read',
    category: 'Operations',
    image: 'assets/container-789488_1280.jpg',
    tags: ['Last-Mile', 'Customer Experience', 'Delivery']
  },
  {
    id: 4,
    title: 'Sustainable Logistics: Green Solutions for Modern Supply Chains',
    excerpt: 'Discover eco-friendly logistics practices that not only reduce environmental impact but also improve efficiency and reduce costs.',
    author: 'David Thompson',
    date: 'February 28, 2024',
    readTime: '8 min read',
    category: 'Sustainability',
    image: 'assets/ship-105596_1280.jpg',
    tags: ['Sustainability', 'Green Logistics', 'Efficiency']
  },
  {
    id: 5,
    title: 'Warehouse Automation: From Manual to Smart Operations',
    excerpt: 'See how automation technologies are transforming warehouse operations and what it means for the future of logistics.',
    author: 'Lisa Wang',
    date: 'February 20, 2024',
    readTime: '6 min read',
    category: 'Automation',
    image: 'assets/warehouse-9006780_1280.jpg',
    tags: ['Automation', 'Warehousing', 'Technology']
  },
  {
    id: 6,
    title: 'Ocean Freight Trends: Navigating the New Normal',
    excerpt: 'Understand the current state of ocean freight and what shippers need to know about capacity, pricing, and service reliability.',
    author: 'Robert Kim',
    date: 'February 15, 2024',
    readTime: '7 min read',
    category: 'Ocean Freight',
    image: 'assets/port-6670684_1280.jpg',
    tags: ['Ocean Freight', 'Global Trade', 'Shipping']
  }
];

const categories = ['All', 'Technology', 'Strategy', 'Operations', 'Sustainability', 'Automation', 'Ocean Freight'];

export default function Blog() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Blog — CROSSDOCS</title>
        <meta name="description" content="Stay updated with the latest insights in logistics, supply chain optimization, and industry trends from CROSSDOCS experts." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 container-consistent text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Logistics Insights &
              <span className="block text-primary-400">Industry Trends</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-200 max-w-3xl mx-auto mb-8">
              Stay ahead of the curve with expert analysis, industry insights, and practical tips 
              from our logistics professionals.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="container-consistent">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            {/* Search Bar */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search articles..."
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-5 w-5 text-slate-600" />
              <span className="text-sm font-medium text-slate-700">Filter by:</span>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      category === 'All'
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    {category}
                  </button>
          ))}
        </div>
      </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-white">
        <div className="container-consistent">
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                {/* Post Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="inline-block px-3 py-1 bg-primary-600 text-white text-xs font-medium rounded-full">
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      {post.author}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {post.date}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {post.readTime}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 mb-3 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                    {post.title}
                  </h3>

                  <p className="text-slate-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Read More Button */}
                  <NavLink
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center gap-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors duration-200 group/btn"
                  >
                    Read More
                    <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform duration-200" />
                  </NavLink>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Load More Button */}
          <div className="text-center mt-12">
            <button className="inline-flex items-center gap-2 bg-primary-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-primary-700 hover:shadow-lg transition-all duration-300">
              Load More Articles
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container-consistent text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Stay Updated with Our Newsletter
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
              Get the latest logistics insights, industry trends, and CROSSDOCS updates 
              delivered directly to your inbox.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200"
              />
              <button className="w-full sm:w-auto bg-primary-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-700 transition-all duration-300">
                Subscribe
              </button>
            </div>
            
            <p className="text-sm text-slate-500 mt-4">
              No spam, unsubscribe at any time. We respect your privacy.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}






